import { FC, MouseEvent, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Avatar, Box, Flex, Menu, MenuButton, Modal, ModalBody, ModalContent, ModalOverlay, Portal, rem, useDisclosure } from '@bonusly/design-system';
import { useBuildMonarchURL } from '@bonusly/url';
import { useCurrentUser, useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import mobileBizySvg from '../../../assets/images/mobile-bizy.svg';
import { BizySidePanel } from '../../../components/bizy-side-panel/bizy-side-panel';
import { resolvePageContext } from '../../../components/bizy-side-panel/lib/bizy-page-config';
import { useBizyPageContextStore } from '../../../components/bizy-side-panel/store/use-bizy-page-context-store';
import { useBizySidePanelStore } from '../../../components/bizy-side-panel/store/use-bizy-side-panel-store';
import { useIsRecognize } from '../../../hooks/use-is-recognize';
import { AppPath } from '../../admin-navbar/config/router';
import { useFetchNotifications } from '../../top-navbar/components/Notification/hooks/useFetchNotifications';
import { CompanyLogo } from './company-logo';
import { NotificationPanel } from './notification';
import { UserMobileNavMenu } from './user-mobile-nav-menu';

const MOBILE_NAVBAR_HEIGHT = 56;
const SPOOFING_BANNER_HEIGHT = 60;

interface UserMobileNavbarProps {
	isSpoofing?: boolean;
}

export const UserMobileNavbar: FC<UserMobileNavbarProps> = ({ isSpoofing = false }) => {
	const user = useCurrentUser();
	const menu = useDisclosure();
	const bizyModal = useDisclosure();
	const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
	const isRecognize = useIsRecognize();
	const monarchURL = useBuildMonarchURL();
	const homeURL = isRecognize ? monarchURL(AppPath.Home) : AppPath.Home;
	const { unreadCount } = useFetchNotifications(user.locale);

	const location = useLocation();
	const isBizyEnabledForUser = useIsBizyEnabledForUser();

	const openPanel = useBizySidePanelStore((state) => state.open);
	const closePanel = useBizySidePanelStore((state) => state.close);
	const isPanelOpen = useBizySidePanelStore((state) => state.isOpen);
	const setBizyPageContext = useBizyPageContextStore((state) => state.setContext);

	const handleBizyOpen = () => {
		setBizyPageContext(resolvePageContext(location.pathname));
		openPanel('user', { isFullscreen: false });
		bizyModal.onOpen();
	};

	const handleBizyClose = () => {
		closePanel();
		bizyModal.onClose();
	};

	const isBizyModalOpen = bizyModal.isOpen;
	const closeBizyModal = bizyModal.onClose;
	useEffect(() => {
		if (!isPanelOpen && isBizyModalOpen) {
			closeBizyModal();
		}
	}, [isPanelOpen, isBizyModalOpen, closeBizyModal]);

	const handleOpenNotifications = () => {
		menu.onClose();
		setIsNotificationPanelOpen(true);
	};

	const handleCloseNotifications = () => {
		setIsNotificationPanelOpen(false);
	};

	const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (isRecognize) {
			e.preventDefault();
			window.location.href = homeURL;
		}
	};

	return (
		<>
			<Flex
				alignItems="center"
				as="nav"
				bg="bg.surface"
				h={rem(MOBILE_NAVBAR_HEIGHT)}
				px={4}
				w="full"
				position="sticky"
				top={isSpoofing ? rem(SPOOFING_BANNER_HEIGHT) : 0}
				zIndex="sticky"
			>
				{/* Logo */}
				<Box
					as={Link}
					id="primary-nav-logo"
					h={rem(48)}
					flex={1}
					minW={0}
					mr={2}
					display="flex"
					alignItems="center"
					overflow="hidden"
					to={homeURL}
					onClick={handleLogoClick}
				>
					<CompanyLogo context="mobile" />
				</Box>

				{/* Bizy + Profile Avatar */}
				<Flex ml="auto" alignItems="center" gap={2}>
					{isBizyEnabledForUser && (
						<Box
							as="button"
							type="button"
							aria-label="Bizy"
							onClick={handleBizyOpen}
							display="flex"
							alignItems="center"
							justifyContent="center"
							cursor="pointer"
							borderRadius="full"
							_active={{ transform: 'scale(0.9)' }}
							transitionDuration="normal"
							transitionProperty="common"
						>
							<Box as="img" src={mobileBizySvg} alt="" w={rem(40)} h={rem(40)} />
						</Box>
					)}
					<Menu placement="bottom-end" isOpen={menu.isOpen} onClose={menu.onClose} onOpen={menu.onOpen}>
						<MenuButton _active={{ transform: 'scale(0.95)' }} transitionDuration="normal" transitionProperty="common" aria-label="Open menu">
							<Box position="relative" display="flex" alignItems="center">
								<Avatar name={user.display_name} src={user.profile_pic_url} h={rem(40)} w={rem(40)} fontSize={1} />
								{unreadCount > 0 && <Box position="absolute" top={rem(-4)} right={rem(-4)} h={rem(10)} w={rem(10)} bg="red.500" borderRadius="full" />}
							</Box>
						</MenuButton>

						<Portal>
							<UserMobileNavMenu onOpenNotifications={handleOpenNotifications} onClose={menu.onClose} />
						</Portal>
					</Menu>
				</Flex>
			</Flex>

			{/* BIZY FULL-SCREEN MODAL */}
			<Modal isOpen={bizyModal.isOpen} onClose={handleBizyClose} size="full">
				<ModalOverlay />
				<ModalContent borderRadius={0} m={0} maxH="100%" h="100%">
					<ModalBody p={0} display="flex" flexDirection="column" h="100%">
						<BizySidePanel gradientTheme="none" panelLeftEdge={0} showFullscreenToggle={false} />
					</ModalBody>
				</ModalContent>
			</Modal>

			{/* Notification Panel */}
			<NotificationPanel isOpen={isNotificationPanelOpen} onClose={handleCloseNotifications} isSidebarExpanded={false} />
		</>
	);
};
